import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { format, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { FinanceKpis } from '@/api/finance';
import { formatFCFA, formatMillions } from '@/utils/currency';

interface RevenueChartProps {
  series:  FinanceKpis[];
  loading?: boolean;
}

interface ChartPoint {
  label:    string;
  ca:       number;
  ebitda:   number;
  periode:  string;
}

/**
 * Évolution CA + EBITDA — pixel-perfect CTO-FRONT-002.
 * Couleurs design system : var(--p) (primary) + var(--p-dark) (secondaire).
 */
export const RevenueChart = ({ series, loading }: RevenueChartProps) => {
  if (loading) {
    return <div style={{ height: 260, background: 'var(--bg-deep)', borderRadius: 'var(--r-sm)' }} className="animate-pulse" />;
  }

  const data: ChartPoint[] = series.map((s) => {
    const month = parse(s.periode, 'yyyy-MM', new Date());
    return {
      label:   format(month, 'MMM', { locale: fr }),
      ca:      Number(s.caFcfaCents)     / 100,
      ebitda:  Number(s.ebitdaFcfaCents) / 100,
      periode: s.periode,
    };
  });

  const allZero = data.every((d) => d.ca === 0 && d.ebitda === 0);

  return (
    <div style={{ position: 'relative', padding: '16px 20px 8px' }}>
      {allZero && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            background: 'rgba(255,255,255,.95)', border: '1px dashed var(--bdr)',
            borderRadius: 'var(--r-sm)', padding: '12px 16px',
            textAlign: 'center', fontSize: 12, color: 'var(--tx-3)',
            boxShadow: 'var(--sh-sm)',
          }}>
            📊 Pas de données comptables sur la période<br />
            <span style={{ color: 'var(--tx-3)', opacity: 0.7 }}>Saisir des écritures pour voir l'évolution</span>
          </div>
        </div>
      )}
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
          <defs>
            <linearGradient id="caGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="var(--p)" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="var(--p)" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="ebitdaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="var(--p-dark)" stopOpacity={0.25}/>
              <stop offset="95%" stopColor="var(--p-dark)" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--bdr)" vertical={false} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'var(--tx-3)', fontFamily: 'var(--font-b)' }}
            tickMargin={8}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'var(--tx-3)', fontFamily: 'var(--font-m)' }}
            tickFormatter={(v: number) => formatMillions(v * 100).replace(' FCFA', '')}
            width={42}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0].payload as ChartPoint;
              return (
                <div style={{
                  background: 'var(--surface)', border: '1px solid var(--bdr)',
                  borderRadius: 'var(--r-sm)', padding: 12, boxShadow: 'var(--sh-md)',
                  fontFamily: 'var(--font-b)',
                }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx)' }}>{p.label} {p.periode.slice(0, 4)}</p>
                  <p style={{ marginTop: 4, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--tx-2)' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--p)' }} />
                    CA : <span style={{ fontFamily: 'var(--font-m)', fontWeight: 600, color: 'var(--tx)' }}>{formatFCFA(p.ca * 100)}</span>
                  </p>
                  <p style={{ marginTop: 2, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--tx-2)' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--p-dark)' }} />
                    EBITDA : <span style={{ fontFamily: 'var(--font-m)', fontWeight: 600, color: 'var(--tx)' }}>{formatFCFA(p.ebitda * 100)}</span>
                  </p>
                </div>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="ca"
            stroke="var(--p)"
            strokeWidth={2.5}
            fill="url(#caGradient)"
            dot={{ r: 3, fill: 'var(--p)', strokeWidth: 0 }}
            activeDot={{ r: 5 }}
          />
          <Area
            type="monotone"
            dataKey="ebitda"
            stroke="var(--p-dark)"
            strokeWidth={2}
            fill="url(#ebitdaGradient)"
            dot={{ r: 2.5, fill: 'var(--p-dark)', strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, fontSize: 11, color: 'var(--tx-3)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--p)' }} />
          Chiffre d'affaires
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--p-dark)' }} />
          EBITDA
        </span>
      </div>
    </div>
  );
};
